import { useCallback, useState } from 'react';
import { Route, withRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import { MsalProvider } from '@azure/msal-react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin, appInsights, logError} from './common/utilities/AppInsightsUtil';
import appSettings from './appSettings';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

// Material-UI Components
import { ThemeProvider, responsiveFontSizes } from '@mui/material/styles';

// Components
import theme from './common/theme/Theme';
import CiamAgentTheme from './common/theme/CiamAgentTheme';
import Loading from './components/common/Loading';
import { Header, Footer, Chatbot, ContactUsFlyout, ShopfloorFooter, ExpirationComponent } from './components';
import { AgentShopfloor, WelcomePage, UserFlowPage, SubmissionPage, CiamIdRecoveryPage, ModernLinkLoginPage, ErrorPage, WebAlerts } from './pages';
import constants from './constants';

function App({ dispatch, pca, internalAuth, ...props }) {
  const [showChatbot, setShowChatbot] = useState(false);
  const [firstEntry, setFirstEntry] = useState(false);
  const [entryFromFederation, setEntryFromFederation] = useState(false);

  const toggleChatbot = useCallback(() => {
    setShowChatbot(!showChatbot);
  }, [showChatbot]);

  appInsights.addTelemetryInitializer((env) => {
    env.data = env.data || {};
    env.data["am-app-name"] = appSettings.APPINSIGHTS_APP_NAME;
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };
  const authPaths = ['/', '/account-preferences', '/advanced-search', '/add-user', '/signup', '/federated', '/internal/manage-alerts'];
  const location = useLocation();

  if (props.location.pathname === '/signup' || firstEntry) {
    pca.controller.config.auth.authority = appSettings.AZURE_SIGNUP;
  } else if (props.location.pathname === '/federated' || entryFromFederation) {
    pca.controller.config.auth.authority = appSettings.AZURE_FEDERATED;
  } 

  if (props.location.hash !== undefined && props.location.hash.includes('#error')) {
    const authority = pca && pca.controller && pca.controller.config && pca.controller.config.auth && pca.controller.config.auth.authority;    
    const [beforeError, afterError] = props.location.hash.split('#error');
    const baseUrl = beforeError;
    const errorFragment = afterError ? `#error${afterError}` : ''
    logError(`Error Event: CIAM Error - Authority: ${authority} - Error Description: ${errorFragment}`, SeverityLevel.Error, baseUrl);
    pca.logoutRedirect().catch(e => {
      window.location.replace(appSettings.AZURE_AD_CLIENT_REDIRECT_URI);
    });
  }
  
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
    <Provider store={store}>
      <ThemeProvider theme={responsiveFontSizes(theme)}>
        <Loading />
        <ThemeProvider theme={CiamAgentTheme}>
          {authPaths.includes(location.pathname) ? null : <Header /> }
          <MsalProvider instance={pca}>
            <ExpirationComponent />
          </MsalProvider>
          <Route path="/no-agency">
            <ErrorPage from={constants.agency}/>
          </Route>
          <Route path="/user-error">
            <ErrorPage from='user'/>
          </Route>
          <Route exact path='/submission' render={(props) => (
            <MsalProvider instance={pca}>
              <SubmissionPage {...props} authRequired={true} />
            </MsalProvider>
          )}/> 
          <Route exact path='/signup' render={(props) => (
            <MsalProvider instance={pca}>
              <AgentShopfloor {...props} authRequired={true} scrollToTop={scrollToTop} setFirstEntry={setFirstEntry} setEntryFromFederation={setEntryFromFederation}/>
            </MsalProvider>
          )}
          />
          <Route exact path='/federated' render={(props) => (
            <MsalProvider instance={pca}>
              <AgentShopfloor {...props} authRequired={true} scrollToTop={scrollToTop} setFirstEntry={setFirstEntry} setEntryFromFederation={setEntryFromFederation}/>
            </MsalProvider>
          )}
          />
          <Route exact path='/userFlow' render={(props) => (<UserFlowPage {...props} />)}/>          
          <Route exact path='/welcome' render={(props) => (<WelcomePage  agentType='newAgent' scrollToTop={scrollToTop}/>)} />
          <Route exact path='/welcomeAgent' render={(props) => (<WelcomePage agentType='newAgent' scrollToTop={scrollToTop}/>)} />
          <Route exact path='/welcomeNewAgent' render={(props) => (<WelcomePage agentType='newAgent' scrollToTop={scrollToTop}/>)} />
          <Route exact path='/welcomeLegacyAgent' render={(props) => (<WelcomePage agentType='originLegacy' scrollToTop={scrollToTop}/>)} />
          <Route exact path='/userIdRecovery' render={(props) => (<CiamIdRecoveryPage scrollToTop={scrollToTop} toggleChatbot={toggleChatbot}/>)} />
          <Route exact path='/add-user' render={(props) => (
            <MsalProvider instance={pca}>
              <AgentShopfloor {...props} authRequired={true} scrollToTop={scrollToTop} toggleChatbot={toggleChatbot} openChatbot={showChatbot} setFirstEntry={setFirstEntry} setEntryFromFederation={setEntryFromFederation}/>
            </MsalProvider>
          )}
          /> 
          <Route exact path='/modernLinkLogin' render={(props) => (<ModernLinkLoginPage scrollToTop={scrollToTop}/>)} />
          <Route exact path='/account-preferences' render={(props) => (
            <MsalProvider instance={pca}>
              <AgentShopfloor {...props} authRequired={true} scrollToTop={scrollToTop} toggleChatbot={toggleChatbot} openChatbot={showChatbot} setFirstEntry={setFirstEntry} setEntryFromFederation={setEntryFromFederation}/>
            </MsalProvider>
          )}
          />
          <Route exact path='/advanced-search' render={(props) => (
            <MsalProvider instance={pca}>
              <AgentShopfloor {...props} authRequired={true} scrollToTop={scrollToTop} toggleChatbot={toggleChatbot} openChatbot={showChatbot} setFirstEntry={setFirstEntry} setEntryFromFederation={setEntryFromFederation}/>
            </MsalProvider>
          )}
          />
          <Route exact path='/internal/manage-alerts' render={(props) => (
            <MsalProvider instance={internalAuth}>
              <WebAlerts {...props} authRequired={true} />
            </MsalProvider>
          )}
          />
          <Route exact path='/' render={(props) => (
            <MsalProvider instance={pca}>
              <AgentShopfloor {...props} authRequired={true} scrollToTop={scrollToTop} toggleChatbot={toggleChatbot} openChatbot={showChatbot} setFirstEntry={setFirstEntry} setEntryFromFederation={setEntryFromFederation}/>
            </MsalProvider>
          )}
          />
            <ContactUsFlyout toggleChatbot={toggleChatbot} />
           {!authPaths.includes(location.pathname) && (
             <Chatbot toggleChatbot={toggleChatbot} openChatbot={showChatbot}/>
           )}
           {authPaths.includes(location.pathname) ? <ShopfloorFooter /> : <Footer /> }
          </ThemeProvider>
        </ThemeProvider>
      </Provider>
    </AppInsightsContext.Provider>
  );
}

export default withRouter(App);
